import { sendGAEvent } from '../utils';

export enum EVipEvents {
  VIP_TRANSFER_FORM_STARTED = 'vip_transfer_form_started',
  VIP_TRANSFER_FORM_REQUESTED = 'vip_transfer_form_requested',
  VIP_TRANSFER_FORM_FAILED = 'vip_transfer_form_failed',
}

const VIP_EVENT_CATEGORY = 'vipEvents' as const;

export interface IVipTransferFormFailedData {
  /**
   * GA taxonomy codes describing what caused the failure, e.g.
   * `['missing_file_1']` or `['file_too_large_file_1', 'invalid_file_format_file_2']`.
   */
  error_data: string[];
  /** Whether file 1 (email screenshot) was attached at click time. */
  file_1_attached: boolean;
  /** Whether file 2 (status screenshot) was attached at click time. */
  file_2_attached: boolean;
  /** Format of file 1 if attached (e.g. `jpeg`, `png`, `pdf`), null otherwise. */
  file_1_format: string | null;
  /** Format of file 2 if attached, null otherwise. */
  file_2_format: string | null;
  /** Size of file 1 in KB, 0 if not attached. */
  file_1_size_kb: number;
  /** Size of file 2 in KB, 0 if not attached. */
  file_2_size_kb: number;
  /** Raw backend/network error message, when available. */
  error_data_raw?: string;
}

/**
 * @description Sends a Google Analytics event when the user uploads their first
 * file within a single attempt of the VIP transfer form.
 * The event name is `vip_transfer_form_started` and the category is `vipEvents`.
 */
export const sendVipTransferFormStartedEvent = () => {
  sendGAEvent(EVipEvents.VIP_TRANSFER_FORM_STARTED, {
    event_category: VIP_EVENT_CATEGORY,
  });
};

/**
 * @description Sends a Google Analytics event when the VIP transfer files are
 * successfully sent to the VIP team.
 * The event name is `vip_transfer_form_requested` and the category is `vipEvents`.
 */
export const sendVipTransferFormRequestedEvent = () => {
  sendGAEvent(EVipEvents.VIP_TRANSFER_FORM_REQUESTED, {
    event_category: VIP_EVENT_CATEGORY,
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description Sends a Google Analytics event when the VIP transfer form
 * submission fails after clicking the "Send request" button.
 * The event name is `vip_transfer_form_failed` and the category is `vipEvents`.
 * The data object contains the failure codes, per-file attachment state,
 * formats and sizes.
 */
export const sendVipTransferFormFailedEvent = (
  data: IVipTransferFormFailedData
) => {
  sendGAEvent(EVipEvents.VIP_TRANSFER_FORM_FAILED, {
    event_category: VIP_EVENT_CATEGORY,
    ...data,
  });
};
